import type { AdInputLookup } from '@finn-no/mobility-item-insertion-shared';
import { Trans } from '@lingui/react/macro';
import { Button } from '@warp-ds/react';
import type React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import type { WidgetModelArray } from '../../../types/widget.js';
import { getHuckConfig } from '../../utils/getHuckConfig.js';
import { useWizard } from './WizardContext.js';
import { WizardErrorSummary } from './WizardErrorSummary.js';

interface WizardNavigationProps {
  onNext?: () => Promise<boolean> | boolean;
  onPrev?: () => Promise<boolean> | boolean;
  onFinish?: () => Promise<void> | void;
  isLoading?: boolean;
  numberOfSteps: number;
  hasVehicleLookup: boolean;
  adInputLookup: AdInputLookup;
  widgetModel: WidgetModelArray;
  mode?: 'create' | 'edit';
  experimentVariant?: string;
  /** Enable back navigation from step 2 to lookup (TAI-1899) */
  enableBackToLookup?: boolean;
}

export const WizardNavigation: React.FC<WizardNavigationProps> = ({
  onNext,
  onPrev = () => Promise.resolve(true),
  onFinish,
  isLoading = false,
  numberOfSteps,
  hasVehicleLookup,
  adInputLookup,
  widgetModel,
  mode,
  experimentVariant,
  enableBackToLookup = false,
}) => {
  const config = getHuckConfig();
  const buttonMode = mode ?? config.mode;
  const step5CtaVariant = experimentVariant ?? config.step5CtaExperimentVariant;
  const navigate = useNavigate();
  const { step: currentStepFromPath } = useParams<{ step: string }>();
  const { currentStep, isLastStep, canGoNext, canGoPrev } = useWizard();
  const {
    formState: { errors },
  } = useFormContext();
  const offset = hasVehicleLookup ? 2 : 1; // +1 to convert 0-based to 1-based, +1 to account for vehicle lookup (if present)
  const displayStep = currentStep + offset;
  const displayTotal = numberOfSteps + (hasVehicleLookup ? 1 : 0); // +1 to account for vehicle lookup (if present)
  const hasErrors = Object.keys(errors).length > 0;

  const goToNext = () => {
    navigate(`/item/${config.itemId}/steps/${Number(currentStepFromPath) + 1}`);
  };

  const goToPrev = () => {
    navigate(`/item/${config.itemId}/steps/${Number(currentStepFromPath) - 1}`);
  };

  const handleNext = async () => {
    if (onNext) {
      const canProceed = await onNext();
      if (canProceed) {
        goToNext();
      }
    } else {
      goToNext();
    }
  };

  const goToLookup = () => {
    const orgId = new URLSearchParams(window.location.search).get('orgId');
    const regnr = config.item?.data?.registration_number;
    // Use vehicle_lookup.variant_id which is populated by vehicle-lookup-service
    const variantId = config.item?.data?.vehicle_lookup?.variant_id;

    // Build URL path: /item/:itemId/lookup[/:regnr[/:variantId]]
    let path = `${config.root}/item/${config.itemId}/lookup`;
    if (regnr) {
      path += `/${encodeURIComponent(regnr)}`;
      if (variantId) {
        path += `/${encodeURIComponent(variantId)}`;
      }
    }
    window.location.assign(`${path}${orgId ? `&orgId=${orgId}` : ''}`);
  };

  const handlePrev = async () => {
    if (onPrev) {
      const canProceed = await onPrev();
      if (canProceed) {
        // Check if we're on the first wizard step and should navigate back to lookup (TAI-1899)
        if (currentStep === 0 && hasVehicleLookup && enableBackToLookup) {
          goToLookup();
          return;
        }
        goToPrev();
      }
    } else {
      // Check if we're on the first wizard step and should navigate back to lookup (TAI-1899)
      if (currentStep === 0 && hasVehicleLookup && enableBackToLookup) {
        goToLookup();
        return;
      }
      goToPrev();
    }
  };

  const handleFinish = async () => {
    if (onFinish) {
      await onFinish();
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0" style={{ zIndex: 11 }}>
      <div
        style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 90%, #f5f5f5dd 100%)',
          height: '60px',
          pointerEvents: 'none',
          margin: '0 auto',
        }}
      />
      <div className="s-bg max-w-[700] mx-auto">
        <div className="h-4 w-full s-bg-primary-subtle">
          <div className="h-4 s-bg-primary" style={{ width: `${(displayStep / displayTotal) * 100}%` }} />
        </div>

        {hasErrors && <WizardErrorSummary adInputLookup={adInputLookup} widgetModel={widgetModel} />}

        <div className="flex items-center gap-16 p-16" style={{ position: 'relative' }}>
          {/* TAI-1899: Show back button on first step when back-to-lookup is enabled */}
          {(canGoPrev || (currentStep === 0 && hasVehicleLookup && enableBackToLookup)) && (
            <Button quiet onClick={handlePrev} loading={isLoading} data-testid="prev-button">
              <Trans id="mobility-item-insertion.wizard.navigation.prev">Back</Trans>
            </Button>
          )}
          {/* NOTE: Minor adjustment for step indicator positioning in the last step when button text takes more space */}
          <div className={`t4 absolute -translate-x-1/2 ${isLastStep ? 'left-2/5 sm:left-1/2' : 'left-1/2'}`}>
            <span data-testid="footer-current-step">{displayStep}</span> / {displayTotal}
          </div>

          {isLastStep ? (
            <Button primary onClick={handleFinish} loading={isLoading} className="ml-auto" data-testid="finish-button">
              {buttonMode === 'create' && step5CtaVariant === 'treatment' ? (
                <Trans
                  id="mobility-item-insertion.wizard.navigation.choose-package"
                  comment="Button text for choosing ad package on the final step"
                >
                  Choose ad package
                </Trans>
              ) : buttonMode === 'create' ? (
                <Trans
                  id="mobility-item-insertion.wizard.navigation.finish"
                  comment="Button text for continuing to the next step in the form or finish the form"
                >
                  Create ad
                </Trans>
              ) : (
                <Trans
                  id="mobility-item-insertion.button.save"
                  comment="Button text for saving changes to an existing ad"
                >
                  Save
                </Trans>
              )}
            </Button>
          ) : (
            <Button
              primary
              onClick={handleNext}
              loading={!canGoNext || isLoading}
              className="ml-auto"
              data-testid="next-button"
            >
              <Trans id="mobility-item-insertion.wizard.navigation.next">Next</Trans>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
